import BookingSummarySkeleton from '@/components/booking-summary/BookingSummarySkeleton';
import MobileBookingSummary from '@/components/booking-summary/MobileBookingSummary';
import RegularBookingSummary from '@/components/booking-summary/RegularBookingSummary';
import Consents from '@/components/Consents';
import Footer from '@/components/Footer';
import PriceDisplay from '@/components/PriceDisplay';
import AncillaryList from '@/components/purchase/checkout/ancillaries/AncillaryList';
import CancelExchangeButton from '@/components/purchase/checkout/cancel-exchange/CancelExchangeButton';
import CancelPurchaseButton from '@/components/purchase/checkout/cancel-purchase/CancelPurchaseButton';
import BookingExpirationTimer from '@/components/purchase/checkout/expiration-timer/BookingExpirationTimer';
import PassengerList from '@/components/purchase/checkout/passengers/PassengerList';
import PaymentModal from '@/components/purchase/checkout/payment-modal/PaymentModal';
import PromoCodes from '@/components/purchase/checkout/promo-codes/PromoCodes';
import Purchaser from '@/components/purchase/checkout/purchaser/Purchaser';
import { SeatSelection } from '@/components/purchase/checkout/seat-selection/SeatSelection';
import SectionSkeleton from '@/components/purchase/checkout/SectionSkeleton';
import Vouchers from '@/components/purchase/checkout/vouchers';
import {
  consentsSelector,
  salesPointConfigurationSelector,
  salesPointPaymentTypesSelector,
} from '@/features/configuration/configurationSelector';
import {
  checkoutLoadingSelector,
  initiatePaymentLoadingSelector,
} from '@/features/loading/loadingSelectors';
import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import {
  abandonExchange,
  getPayableAmount,
  getPurchaseFlowTravelAccountsMap,
  initiateAdyenPayment,
  initiateExternalPayment,
  initiateKlixPayment,
  initiateZeroPayment,
  refreshCheckoutBooking,
  updateBookingPassengers,
  updatePurchaseFlowBookingPurchaser,
} from '@/features/purchase/purchaseActions';
import {
  exchangeOperationIdSelector,
  purchaseFlowBookingSelector,
  purchaseFlowTravelAccountsMapSelector,
} from '@/features/purchase/purchaseSelectors';
import { validateAdyenCheckoutPaymentDetails } from '@/features/purchase/purchaseService';
import { useAuth } from '@/hooks/useAuth';
import useBookingSummaryData from '@/hooks/useBookingSummaryData';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useChangeTotal } from '@/hooks/useChangeTotal';
import { usePaymentStatusCheck } from '@/hooks/usePaymentStatusCheck';
import type {
  PassengerFieldNames,
  PurchaserFieldNames,
} from '@/hooks/usePersonFieldRenderer';
import { usePersonFieldRenderer } from '@/hooks/usePersonFieldRenderer';
import { STORAGE_KEYS, useStorage } from '@/hooks/useStorage';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { useTranslation } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import type { TransTextKeys } from '@/i18n/trans/text';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import type { BookingDTO, PayWithAdyenResponseDTO } from '@/types/dto/agent';
import type { BookingPassenger } from '@/types/passenger';
import type { PurchaseFlowBookingStorageData } from '@/types/storage';
import {
  getBookingFulfillments,
  getBookingNonTripOfferFulfillments,
  getBookingTotalPrice,
  isTicketBooking,
  isTravelPassBooking,
} from '@/utils/booking';
import { breakpoints } from '@/utils/breakpoints';
import {
  GTM_EVENT,
  mapBookingToEcommerceItems,
  trackEcommerceEventV2,
} from '@/utils/gtm';
import {
  buildPaymentType,
  getBookingAncillaries,
  getBookingPromoCodes,
  getBookingVoucherCodes,
  mapBookedAncillaryToExtrasItem,
  mapBookingToGTMTripItems,
} from '@/utils/gtmPayloads';
import { isLocalDevelopment } from '@/utils/isLocalDev';
import { getLocalIpAddress } from '@/utils/location';
import {
  bookingPassengerToFormValues,
  getPassengerGenderFromProfileGender,
} from '@/utils/passenger';
import { phonenumberToE164 } from '@/utils/phonenumberToE164';
import { purchaseFlowJourneyOrOfferPaths } from '@/utils/purchase';
import { type CheckoutValues, createCheckoutSchema } from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertBox,
  Button,
  Card,
  Dialog,
  Divider,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

const ONLINE_PAYMENT_TYPE_IDS = {
  PAYMENT_TYPE: {
    adyen: 'PAYMENT_TYPE.ADYEN',
    klix: 'PAYMENT_TYPE.KLIX',
  },
  SALES_POINT_PAYMENT_TYPE: {
    adyen: 'SALES_POINT_PAYMENT_TYPE.ADYEN_CHECKOUT',
    klix: 'SALES_POINT_PAYMENT_TYPE.KLIX',
  },
} as const;

interface CheckoutProps {
  onExchangeExit?: () => void;
  onExchangeSuccess?: () => void;
}

const Checkout = ({ onExchangeExit, onExchangeSuccess }: CheckoutProps) => {
  const dispatch = useDispatch();
  const loading = useSelector(checkoutLoadingSelector);
  const initiatePaymentLoading = useSelector(initiatePaymentLoadingSelector);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const purchaseBooking = useSelector(purchaseFlowBookingSelector);
  const manageBooking = useSelector(manageFlowBookingSelector);
  const { changeFlow, hasSelection } = useChangeBookingSelection();
  const salesPointPaymentTypes = useSelector(salesPointPaymentTypesSelector);
  const changeTotal = useChangeTotal();
  const booking = hasSelection ? manageBooking : purchaseBooking;
  const originalBookingHadAncillaries = (manageBooking?.bookedTrips ?? []).some(
    (trip) =>
      (trip.bookedOffers ?? []).some((offer) =>
        (offer.admissions ?? []).some(
          (admission) => (admission.ancillaries?.length ?? 0) > 0
        )
      )
  );
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const profile = useSelector(profileDataSelector);
  const salesPointConfiguration = useSelector(salesPointConfigurationSelector);
  const consents = useSelector(consentsSelector);
  const { setValue: setCheckoutSuccessSessionStorage } =
    useStorage<PurchaseFlowBookingStorageData>(
      sessionStorage,
      STORAGE_KEYS.CHECKOUT_SUCCESS_DATA
    );
  const [paymentSession, setPaymentSession] =
    useState<PayWithAdyenResponseDTO | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [searchParams] = useSearchParams();
  const redirectResult = searchParams.get('redirectResult');
  const [isRedirectReturn, setIsRedirectReturn] = useState(false);
  const [isRedirectingToKlix, setIsRedirectingToKlix] = useState(false);
  const [forceShowPassengers, setForceShowPassengers] = useState(false);
  const tenantConfiguration = useTenantConfiguration();
  const onlinePaymentTypeIds = tenantConfiguration
    ? ONLINE_PAYMENT_TYPE_IDS[tenantConfiguration.paymentClassificationGroup]
    : undefined;
  const isPaymentTypeAllowed = (paymentTypeId?: string) =>
    !!paymentTypeId &&
    (salesPointPaymentTypes?.paymentTypes?.some(
      (paymentType) =>
        paymentType.id === paymentTypeId &&
        (hasSelection
          ? paymentType.isAllowedForModification
          : paymentType.isAllowedForPurchase)
    ) ??
      false);
  const canPayWithKlix = isPaymentTypeAllowed(onlinePaymentTypeIds?.klix);
  const canPayWithAdyen = isPaymentTypeAllowed(onlinePaymentTypeIds?.adyen);
  const travelAccountsMap = useSelector(purchaseFlowTravelAccountsMapSelector);

  const shouldBlock = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: { pathname: string };
      nextLocation: { pathname: string };
    }) =>
      currentLocation.pathname === '/purchase/checkout' &&
      purchaseFlowJourneyOrOfferPaths.includes(nextLocation.pathname),
    []
  );

  useBlocker(shouldBlock);

  useEffect(() => {
    if (!booking || (booking && Object.keys(booking).length === 1)) {
      // The journey-change flow has no /purchase to fall back to.
      if (onExchangeExit) {
        onExchangeExit();
        return;
      }
      navigate('/purchase');
    }
  }, [booking, booking?.id, navigate, onExchangeExit]);

  useEffect(() => {
    const fulfillments = getBookingNonTripOfferFulfillments(booking);

    if (isTravelPassBooking(booking) && fulfillments.length) {
      dispatch(getPurchaseFlowTravelAccountsMap(fulfillments));
    }
  }, [booking, dispatch]);

  useEffect(() => {
    if (hasSelection && booking?.id) {
      dispatch(getPayableAmount());
    }
  }, [hasSelection, booking, dispatch]);

  const exchangeOperationId = useSelector(exchangeOperationIdSelector);
  const exchangeOperationIdRef = useRef(exchangeOperationId);
  exchangeOperationIdRef.current = exchangeOperationId;
  const paymentCompletedRef = useRef(false);
  const hasFiredBeginCheckoutEvent = useRef(false);
  const hasFiredViewBookingEvent = useRef(false);
  // Payment rail chosen by the user, persisted into CHECKOUT_SUCCESS_DATA so
  // the success page can attach payment_type to the purchase event (BR-57983).
  const paymentTypeRef = useRef<string>();

  // Shared ecommerce payload for the checkout/payment events. Keeps the
  // GA4-format admission `items` (other tenants' containers depend on that
  // shape) and adds the TIF tagging-plan fields alongside.
  const buildEcommerceEventPayload = useCallback(() => {
    const promoCodes = getBookingPromoCodes(booking);
    const voucherCodes = getBookingVoucherCodes(booking);

    return {
      currency: booking?.provisionalPrice?.currency,
      value: booking?.provisionalPrice?.amount,
      promo_codes: promoCodes.length ? promoCodes : undefined,
      vouchers: voucherCodes.length ? voucherCodes : undefined,
      total_passenger: booking?.passengers?.length,
      round_trip: (booking?.bookedTrips?.length ?? 0) > 1,
      items: mapBookingToEcommerceItems(booking),
      extras_items: getBookingAncillaries(booking).map(
        mapBookedAncillaryToExtrasItem
      ),
    };
  }, [booking]);

  // Tagging plan (BR-57983): one view_booking push per checkout visit, once
  // the booking with its price is available.
  useEffect(() => {
    if (hasFiredViewBookingEvent.current || !booking?.provisionalPrice) {
      return;
    }
    hasFiredViewBookingEvent.current = true;

    trackEcommerceEventV2(GTM_EVENT.VIEW_BOOKING, {
      currency: booking.provisionalPrice.currency,
      value: booking.provisionalPrice.amount,
      total_passenger: booking.passengers?.length,
      round_trip: (booking.bookedTrips?.length ?? 0) > 1,
      items: mapBookingToGTMTripItems(booking),
      extras_items: getBookingAncillaries(booking).map(
        mapBookedAncillaryToExtrasItem
      ),
    });
  }, [booking]);
  useEffect(() => {
    return () => {
      if (paymentCompletedRef.current) return;
      if (exchangeOperationIdRef.current) {
        dispatch(abandonExchange());
      }
    };
  }, [dispatch]);

  const markPaymentCompleted = useCallback(() => {
    paymentCompletedRef.current = true;
  }, []);

  const { isConfirmingKlixPayment, setKlixPaymentPending } =
    usePaymentStatusCheck({
      booking,
      isChangeFlow: hasSelection,
      onPaymentCompleted: markPaymentCompleted,
    });

  const {
    passengerAdmissionFields,
    passengerTravelPassFields,
    purchaserFields,
  } = usePersonFieldRenderer();

  const passengerFields = isTicketBooking(booking)
    ? passengerAdmissionFields
    : passengerTravelPassFields;

  const getPassengerDefaultValuesForConfigurableFields = useCallback(
    (
      passenger: BookingPassenger,
      index: number
    ): Record<PassengerFieldNames, string> => {
      if (
        index === 0 &&
        isAuthenticated &&
        profile &&
        ![passenger.firstName, passenger.lastName].join('')
      ) {
        return {
          firstName: profile.firstName ?? '',
          lastName: profile.lastName ?? '',
          email: profile.email ?? '',
          phoneNumber:
            phonenumberToE164({
              phonenumber: profile.phoneNumber,
              countryCode: tenantConfiguration?.defaultCountry,
            }) ??
            profile.phoneNumber ??
            '',
          birthDate: profile.birthDate?.split('T')[0] ?? '',
          gender: getPassengerGenderFromProfileGender(profile.gender?.id) ?? '',
          zipCode: profile.zipCode ?? '',
          stateCode: '',
          companyUnitCode: '',
        };
      }
      return bookingPassengerToFormValues(passenger);
    },
    [isAuthenticated, profile, tenantConfiguration?.defaultCountry]
  );

  const getPurchaserDefaultValuesForConfigurableFields = useCallback(
    (p: BookingDTO['purchaser']): Record<PurchaserFieldNames, string> => ({
      firstName: p?.firstName ?? '',
      lastName: p?.lastName ?? '',
      email: p?.contactInformation?.emailAddress ?? '',
      phoneNumber: p?.contactInformation?.phoneNumber ?? '',
      streetName: p?.address?.streetName ?? '',
    }),
    []
  );

  const form = useForm<CheckoutValues>({
    mode: 'onBlur',
    resolver: zodResolver(
      createCheckoutSchema(t, passengerFields, purchaserFields)
    ),
    defaultValues: {
      passengers:
        booking?.passengers?.map((p, index) => ({
          id: p.id,
          externalReference: p.externalReference,
          ...getPassengerDefaultValuesForConfigurableFields(p, index),
        })) ?? [],
      purchaser: {
        externalReference: booking?.purchaser?.externalReference,
        ...getPurchaserDefaultValuesForConfigurableFields(booking?.purchaser),
      },
      consents:
        consents?.items?.map(({ id, isMandatory, description, code }) => ({
          isMandatory,
          description,
          checked: false,
          id,
          code,
        })) ?? [],
    },
  });

  const { trigger, watch } = form;
  const currentPassengers = watch('passengers');
  const currentPurchaser = watch('purchaser');

  const sections: Array<{
    required: boolean;
    section: ReactNode;
    paragraphKey?: TransTextKeys;
    descriptionKey?: TransTextKeys;
    skeletonType:
      | 'passengers'
      | 'seatSelection'
      | 'ancillaries'
      | 'purchaser'
      | 'promoCodes'
      | 'vouchers'
      | 'consents';
  }> = useMemo(
    () => [
      ...(!hasSelection || forceShowPassengers
        ? [
            {
              section: <PassengerList formFields={passengerFields} />,
              required: true,
              paragraphKey: 'passengers' as const,
              skeletonType: 'passengers' as const,
            },
          ]
        : []),
      ...(isTicketBooking(booking)
        ? [
            {
              section: <SeatSelection />,
              required: false,
              paragraphKey: 'seatSelection' as const,
              skeletonType: 'seatSelection' as const,
            },
          ]
        : []),
      ...(isTicketBooking(booking) && tenantConfiguration?.showCheckoutExtras
        ? [
            {
              section: (
                <>
                  {hasSelection &&
                    originalBookingHadAncillaries &&
                    changeFlow !== 'changeTravelClass' && (
                      <AlertBox
                        variant="attention"
                        data-testid="original-extras-notice"
                        text={
                          <TransText i18nKey="originalExtrasNotChangedNotice" />
                        }
                      />
                    )}
                  <AncillaryList />
                </>
              ),
              required: false,
              paragraphKey: 'extras' as const,
              skeletonType: 'ancillaries' as const,
            },
          ]
        : []),
      {
        section: <Purchaser formFields={purchaserFields} />,
        required: true,
        paragraphKey: 'payer',
        skeletonType: 'purchaser',
      },
      ...(salesPointConfiguration?.arePromoCodesUsed && !hasSelection // BR-56140 promo code cannot be added during change-trips
        ? [
            {
              section: <PromoCodes />,
              required: false,
              paragraphKey: 'promoCodes' as const,
              descriptionKey: 'promoCodesDescription' as const,
              skeletonType: 'promoCodes' as const,
            },
          ]
        : []),
      ...(salesPointConfiguration?.areVouchersUsed
        ? [
            {
              section: <Vouchers />,
              required: false,
              paragraphKey: 'vouchers' as const,
              descriptionKey: 'vouchersDescription' as const,
              skeletonType: 'vouchers' as const,
            },
          ]
        : []),
      ...(tenantConfiguration?.showCheckoutConsents
        ? [
            {
              section: <Consents />,
              required: true,
              skeletonType: 'consents' as const,
            },
          ]
        : []),
    ],
    [
      booking,
      changeFlow,
      forceShowPassengers,
      hasSelection,
      originalBookingHadAncillaries,
      passengerFields,
      purchaserFields,
      salesPointConfiguration?.arePromoCodesUsed,
      salesPointConfiguration?.areVouchersUsed,
      tenantConfiguration,
    ]
  );

  const handleClosePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
    setPaymentSession(null);
    setIsRedirectReturn(false);
  }, []);

  const handlePaymentSuccess = useCallback(async () => {
    if (!booking?.id || !booking.code) return;

    paymentCompletedRef.current = true;

    if (hasSelection && !onExchangeSuccess) {
      navigate('/manage-booking/details', {
        replace: true,
        state: { completedChangeFlow: true },
      });
      return;
    }

    let bookingToUse = booking;
    try {
      const refreshedBooking = await dispatch(
        refreshCheckoutBooking()
      ).unwrap();
      bookingToUse = refreshedBooking ?? booking;
    } catch (error) {
      console.error('Failed to refresh booking after payment:', error);
    }

    // Discount context is read from the pre-refresh booking — after payment
    // the refreshed booking no longer carries pendingVouchers.
    // Adyen redirect returns remount Checkout, losing the ref — but a
    // redirectResult param on the URL can only come from the Adyen rail.
    const paymentRail =
      paymentTypeRef.current ?? (redirectResult ? 'Adyen' : undefined);

    setCheckoutSuccessSessionStorage({
      bookingId: bookingToUse.id!,
      code: bookingToUse.code!,
      payerEmail: currentPurchaser.email,
      fulfillments: getBookingFulfillments(bookingToUse),
      totalPrice: getBookingTotalPrice(booking),
      paymentType: paymentRail
        ? buildPaymentType(paymentRail, booking)
        : undefined,
      promoCodes: getBookingPromoCodes(booking),
      vouchers: getBookingVoucherCodes(booking),
    });

    if (onExchangeSuccess) {
      onExchangeSuccess();
      return;
    }

    navigate('/success');
  }, [
    booking,
    currentPurchaser.email,
    dispatch,
    hasSelection,
    navigate,
    onExchangeSuccess,
    redirectResult,
    setCheckoutSuccessSessionStorage,
  ]);

  const summaryData = useBookingSummaryData({
    booking,
    passengers: currentPassengers,
    vouchers: booking?.pendingVouchers,
    displayPrice: hasSelection
      ? booking?.confirmedPrice
      : booking?.provisionalPrice,
    travelAccountsMap,
    allowedStatuses: ['FULFILLED', 'PREBOOKED'],
  });

  useEffect(() => {
    const validatePayment = async () => {
      if (!booking?.id) return;

      const threeDsResult = searchParams.get('threeDsResult') || undefined;
      const stateData = searchParams.get('stateData') ? null : undefined;

      if (!redirectResult && !threeDsResult && !stateData) {
        return;
      }

      try {
        await validateAdyenCheckoutPaymentDetails(booking?.id, {
          ...(redirectResult && { redirectResult }),
          ...(threeDsResult && { threeDsResult }),
          ...(stateData !== undefined && { stateData }),
        });
        setIsRedirectReturn(true);
        setShowPaymentModal(true);
      } catch (error) {
        console.error('Payment validation error:', error);
        toast.error(<TransAlert i18nKey="paymentFailed" />);
      }
    };

    validatePayment();
  }, [redirectResult, searchParams, booking?.id]);

  const handleValidations = useCallback(async () => {
    const passengersValid = await trigger('passengers');
    const purchaserValid = await trigger('purchaser');

    if (!passengersValid) {
      setForceShowPassengers(true);
    }

    if (!passengersValid || !purchaserValid) {
      toast.error(<TransAlert i18nKey="mandatoryFieldsMustBeFilled" />);
      return false;
    }

    if (tenantConfiguration?.showCheckoutConsents) {
      const consentsAgreedWith = await trigger('consents');

      if (!consentsAgreedWith) {
        toast.error(<TransAlert i18nKey="consentsNotAgreedWith" />);
        return false;
      }
    }

    return true;
  }, [
    trigger,
    tenantConfiguration?.showCheckoutConsents,
    setForceShowPassengers,
  ]);

  const handlePayment = useCallback(
    async (paymentCallback: () => Promise<void>) => {
      if (!booking?.id) {
        return;
      }

      const isValid = await handleValidations();

      if (!isValid) {
        return;
      }

      try {
        await dispatch(
          updateBookingPassengers({
            bookingId: booking.id,
            passengers: currentPassengers,
          })
        ).unwrap();
        await dispatch(
          updatePurchaseFlowBookingPurchaser(currentPurchaser)
        ).unwrap();

        if (booking.provisionalPrice?.amount) {
          await paymentCallback();
        } else {
          paymentTypeRef.current = 'Free';
          await dispatch(initiateZeroPayment(booking.id)).unwrap();
          handlePaymentSuccess();
        }
      } catch (error) {
        const reason = (error as ApiErrorResponse).response?.data.errors?.[0]
          ?.message;

        toast.error(
          reason ? (
            <div className="flex gap-1">
              <TransAlert i18nKey="paymentFailed" />
              <Typography>{reason}</Typography>
            </div>
          ) : (
            <TransAlert i18nKey="paymentFailed" />
          )
        );
      }
    },
    [
      booking?.id,
      booking?.provisionalPrice?.amount,
      currentPassengers,
      currentPurchaser,
      dispatch,
      handlePaymentSuccess,
      handleValidations,
    ]
  );

  const handleAdyenPayment = useCallback(async () => {
    if (!booking?.id) {
      return;
    }

    paymentTypeRef.current = 'Adyen';
    if (!hasFiredBeginCheckoutEvent.current) {
      hasFiredBeginCheckoutEvent.current = true;
      trackEcommerceEventV2(
        GTM_EVENT.BEGIN_CHECKOUT,
        buildEcommerceEventPayload()
      );
    }

    const payload = {
      bookingId: booking.id,
      returnUrl: window.location.href,
      countryCode: salesPointConfiguration?.countryCode ?? 'US',
      shopperIp: await getLocalIpAddress(),
      firstName: currentPurchaser.firstName,
      lastName: currentPurchaser.lastName,
      email: currentPurchaser.email,
      phoneNumber: currentPurchaser.phoneNumber,
    };

    handlePayment(async () => {
      const data = await dispatch(initiateAdyenPayment(payload)).unwrap();

      setPaymentSession(data);
      setShowPaymentModal(true);
    });
  }, [
    booking,
    buildEcommerceEventPayload,
    currentPurchaser.email,
    currentPurchaser.firstName,
    currentPurchaser.lastName,
    currentPurchaser.phoneNumber,
    dispatch,
    handlePayment,
    salesPointConfiguration?.countryCode,
  ]);

  const handleKlixPayment = useCallback(async () => {
    if (!booking?.id) {
      return;
    }

    paymentTypeRef.current = 'Klix';
    if (!hasFiredBeginCheckoutEvent.current) {
      hasFiredBeginCheckoutEvent.current = true;
      trackEcommerceEventV2(
        GTM_EVENT.BEGIN_CHECKOUT,
        buildEcommerceEventPayload()
      );
    }

    const payload = {
      bookingId: booking.id,
      returnUrl: window.location.href,
      firstName: currentPurchaser.firstName,
      lastName: currentPurchaser.lastName,
      email: currentPurchaser.email,
      phoneNumber: currentPurchaser.phoneNumber,
    };

    handlePayment(async () => {
      setIsRedirectingToKlix(true);
      try {
        const data = await dispatch(initiateKlixPayment(payload)).unwrap();

        if (!data.paymentLink) {
          setIsRedirectingToKlix(false);
          toast.error(<TransAlert i18nKey="paymentFailed" />);
          return;
        }

        setKlixPaymentPending({
          bookingId: payload.bookingId,
        });

        trackEcommerceEventV2(GTM_EVENT.ADD_PAYMENT_INFO, {
          ...buildEcommerceEventPayload(),
          payment_type: buildPaymentType('Klix', booking),
        });

        paymentCompletedRef.current = true;
        window.location.assign(data.paymentLink);
      } catch (error) {
        setIsRedirectingToKlix(false);
        throw error;
      }
    });
  }, [
    booking,
    buildEcommerceEventPayload,
    currentPurchaser.email,
    currentPurchaser.firstName,
    currentPurchaser.lastName,
    currentPurchaser.phoneNumber,
    dispatch,
    handlePayment,
    setKlixPaymentPending,
  ]);

  const handleExternalPayment = useCallback(async () => {
    if (!booking?.id || !tenantConfiguration) {
      return;
    }

    paymentTypeRef.current = 'External';
    // Fixes a pre-existing gap: the external rail never fired begin_checkout.
    if (!hasFiredBeginCheckoutEvent.current) {
      hasFiredBeginCheckoutEvent.current = true;
      trackEcommerceEventV2(
        GTM_EVENT.BEGIN_CHECKOUT,
        buildEcommerceEventPayload()
      );
    }

    const payload = {
      bookingId: booking.id,
      offerPartIds: [],
      paidAmount: {
        amount: booking.provisionalPrice?.amount ?? 0,
        currency: salesPointConfiguration?.currencies?.[0],
      },
      transactionId: uuidv4(),
      typeId: `${tenantConfiguration.paymentClassificationGroup}.EXTERNAL_3RD_PARTY`,
    };

    handlePayment(async () => {
      await dispatch(initiateExternalPayment(payload)).unwrap();
      trackEcommerceEventV2(GTM_EVENT.ADD_PAYMENT_INFO, {
        ...buildEcommerceEventPayload(),
        currency: payload.paidAmount.currency,
        value: payload.paidAmount.amount,
        payment_type: buildPaymentType('External', booking),
      });
      handlePaymentSuccess();
    });
  }, [
    booking,
    buildEcommerceEventPayload,
    dispatch,
    handlePayment,
    handlePaymentSuccess,
    salesPointConfiguration?.currencies,
    tenantConfiguration,
  ]);

  return (
    <FormProvider {...form}>
      <div
        className="mx-auto flex w-full max-w-laptop items-start gap-8 p-4 pb-36 laptop:pb-24"
        data-testid="checkout-view"
      >
        <div className="flex w-full flex-1 flex-col gap-4">
          <div className="sticky top-0 z-10 flex justify-between bg-background py-2">
            {hasSelection ? (
              <h1 className="flex min-w-0 flex-col gap-0.5 laptop:flex-row laptop:items-baseline laptop:gap-2">
                <Typography
                  variant={isLaptopOrBigger ? 'heading1' : 'subtitle'}
                  as="span"
                  className="min-w-0 truncate"
                >
                  <TransText i18nKey={changeFlow ?? 'changeTrips'} />
                  <span className="hidden laptop:inline">:</span>
                </Typography>
                <Typography
                  variant={isLaptopOrBigger ? 'heading1-light' : 'body1'}
                  as="span"
                  className="min-w-0 truncate leading-5 laptop:leading-8"
                >
                  <TransText i18nKey="booking" /> {booking?.code}
                </Typography>
              </h1>
            ) : (
              <Typography variant="heading1" as="h1">
                <TransText i18nKey="checkout" />
              </Typography>
            )}
            {!isLaptopOrBigger &&
              booking &&
              Object.keys(booking).length > 1 && <BookingExpirationTimer />}
          </div>
          {sections.map(
            (
              { paragraphKey, descriptionKey, section, required, skeletonType },
              key
            ) => (
              <Card key={key} className="p-0">
                <div className="flex flex-col gap-3 p-3 pb-4 laptop:p-6 laptop:pt-4">
                  {paragraphKey && (
                    <>
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-0.5">
                          <Typography
                            variant="subtitle"
                            className="flex gap-0.5"
                            as="h2"
                          >
                            <span>{key + 1}.</span>
                            <TransText i18nKey={paragraphKey} />
                          </Typography>
                          <Typography variant="body1" className="text-neutral">
                            {!required && (
                              <>
                                (<TransText i18nKey="optional" />)
                              </>
                            )}
                          </Typography>
                        </div>
                        {descriptionKey && (
                          <Typography variant="body1" className="text-neutral">
                            <TransText i18nKey={descriptionKey} />
                          </Typography>
                        )}
                      </div>
                      <Divider variant="medium" />
                    </>
                  )}
                  <>
                    {loading && <SectionSkeleton type={skeletonType} />}
                    <div className={loading ? 'hidden' : undefined}>
                      {section}
                    </div>
                  </>
                </div>
              </Card>
            )
          )}
        </div>

        {isLaptopOrBigger && (
          <div className="sticky top-0 flex flex-col gap-4">
            <div className="flex justify-between py-2">
              <Typography variant="heading2" as="h2">
                <TransText i18nKey="bookingSummary" />
              </Typography>
              {booking && Object.keys(booking).length > 1 && (
                <BookingExpirationTimer />
              )}
            </div>
            <>
              {loading && <BookingSummarySkeleton variant="regular" />}
              <div className={loading ? 'hidden' : undefined}>
                <RegularBookingSummary {...summaryData} />
              </div>
            </>
          </div>
        )}
      </div>
      <Footer
        content={
          isLaptopOrBigger ? (
            <div className="flex items-center gap-2">
              <Icons.travelPass
                height={16}
                width={16}
                className="text-primary"
              />
              <Typography variant="subtitle">
                <TransText
                  i18nKey={hasSelection ? 'changeTotal' : 'bookingTotal'}
                />
                :
              </Typography>
              <Typography variant="subtitle" className="text-primary">
                {hasSelection ? (
                  changeTotal
                ) : (
                  <PriceDisplay amount={booking?.provisionalPrice?.amount} />
                )}
              </Typography>
            </div>
          ) : (
            <>
              {loading && <BookingSummarySkeleton variant="mobile" />}
              <div className={loading ? 'hidden' : undefined}>
                <MobileBookingSummary {...summaryData} />
              </div>
            </>
          )
        }
        actionButtons={
          <>
            {hasSelection ? (
              <CancelExchangeButton onExit={onExchangeExit} />
            ) : (
              <CancelPurchaseButton />
            )}
            {(tenantConfiguration?.showPayWithExternalPayment ||
              isLocalDevelopment()) && (
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="confirm-purchase-button-external"
                onClick={handleExternalPayment}
                variant="cta"
                className="rounded-lg laptop:w-auto"
                isPending={initiatePaymentLoading}
                isDisabled={isConfirmingKlixPayment || isRedirectingToKlix}
                startAdornment={<Icons.lock />}
              >
                <TransText i18nKey="externalPay" />
              </Button>
            )}
            {canPayWithKlix && (
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="confirm-purchase-button-klix"
                className="rounded-lg laptop:w-auto"
                onClick={handleKlixPayment}
                aria-label="Pay with Klix"
                variant="cta"
                isPending={initiatePaymentLoading}
                isDisabled={isConfirmingKlixPayment || isRedirectingToKlix}
                startAdornment={<Icons.lock />}
              >
                <TransText i18nKey="payWithKlix" />
                {isLaptopOrBigger && (
                  <PriceDisplay amount={booking?.provisionalPrice?.amount} />
                )}
              </Button>
            )}
            {canPayWithAdyen && (
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="confirm-purchase-button"
                className="rounded-lg laptop:w-auto"
                onClick={handleAdyenPayment}
                aria-label="Pay button"
                variant="cta"
                isPending={initiatePaymentLoading}
                isDisabled={isConfirmingKlixPayment || isRedirectingToKlix}
                startAdornment={<Icons.lock />}
              >
                <TransText i18nKey="pay" />
                {isLaptopOrBigger && (
                  <PriceDisplay amount={booking?.provisionalPrice?.amount} />
                )}
              </Button>
            )}
          </>
        }
      />
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={handleClosePaymentModal}
        onPaymentSuccess={handlePaymentSuccess}
        paymentSession={paymentSession}
        isRedirectReturn={isRedirectReturn}
      />
      {(isRedirectingToKlix || isConfirmingKlixPayment) && (
        <Dialog
          isOpen
          onOpenChange={() => {}}
          isDismissable={false}
          isKeyboardDismissDisabled
          hideCloseButton
          headerTitle={<TransText i18nKey="payment" />}
          className={{ content: 'p-0' }}
          data-testid="klix-payment-confirming-modal"
        >
          <div className="flex min-h-[280px] flex-col items-center justify-center p-6">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <Typography
              variant="paragraph"
              className="mt-4 text-center text-dark"
            >
              <TransText
                i18nKey={
                  isRedirectingToKlix
                    ? 'youAreBeingRedirectedToKlix'
                    : 'yourPaymentIsBeingProcessedPleaseWait'
                }
              />
            </Typography>
          </div>
        </Dialog>
      )}
    </FormProvider>
  );
};

export default Checkout;
